import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useAppSelector } from '../../../store';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import Modal from '../../../components/Modal';
import Table, { Column } from '../../../components/Table';
import Icon from '../../../components/Icon';
import {
    FiPlus,
    FiSearch,
    FiCheck,
    FiX,
    FiCalendar,
    FiMapPin,
    FiCornerUpRight,
    FiEdit2,
    FiEye,
    FiTrash2,
    FiDownload,
    FiChevronDown,
    FiFileText,
    FiRefreshCw,
    FiSend
} from 'react-icons/fi';
import { fetchBranchesData, type Branch } from '../../branches/service/branchesService';
import {
    createTravellingApprovalRequest,
    fetchTravellingApprovalRequests,
    updateTravellingApprovalStatus,
    updateTravellingApprovalRequest,
    deleteTravellingApprovalRequest,
    type TravellingApprovalRequest
} from '../service/travellingApprovalService';
import { fetchManagedPsoIds } from '../../protocol/service/protocolService';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

const formatDateGlobal = (dateVal: string | Date | undefined): string => {
    if (!dateVal) return '—';
    const d = dayjs(dateVal);
    if (!d.isValid()) return '—';
    return d.format('MMMM D, YYYY');
};

export default function TravellingApproval() {
    const pso = useAppSelector((s) => s.user.pso);
    const bank = useAppSelector((s) => s.user.bank);

    const bankId = bank?.bankDocId || sessionStorage.getItem('bankDocId') || '';
    const psoDocId = sessionStorage.getItem('psoDocId') || '';
    const userRole = sessionStorage.getItem('role') || '';
    const designation = pso?.designation || '';

    const reporterName = userRole === 'bank'
        ? (bank?.companyName || 'Bank User')
        : (pso?.name || 'Unknown User');
    const reporterDesignation = userRole === 'bank'
        ? 'Bank Representative'
        : (pso?.designation || 'PSO');

    const isInchargeHR = designation.toLowerCase().replace(/_/g, ' ') === 'incharge hr';
    const isRegional = /regional/i.test(designation);
    const isHead = /(^|\s)head(\s|$)/i.test(designation.replace(/_/g, ' ')) && !isRegional;
    const isPSO = userRole === 'pso' && !isRegional && !isHead && !isInchargeHR;
    const isBank = userRole === 'bank';
    const isApprover = isRegional || isHead || isBank || isInchargeHR;

    const [requests, setRequests] = useState<TravellingApprovalRequest[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [updatingId, setUpdatingId] = useState<string | null>(null);

    // Form States
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<TravellingApprovalRequest | null>(null);
    const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
    const [dateOfVisit, setDateOfVisit] = useState('');
    const [lastDateOfVisit, setLastDateOfVisit] = useState('');
    const [branchSearch, setBranchSearch] = useState('');
    const [distanceInput, setDistanceInput] = useState('0');
    const [remarks, setRemarks] = useState('');
    const [purpose, setPurpose] = useState('survey');
    const [purposeOtherDetail, setPurposeOtherDetail] = useState('');
    const [travelModality, setTravelModality] = useState('');
    const [nightStay, setNightStay] = useState(false);
    const [amountClaim, setAmountClaim] = useState('0');
    const [confirmAmountInput, setConfirmAmountInput] = useState('');
    const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsBranchDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter States
    const defaultStatus = useMemo(() => {
        if (isHead) return 'forwarded';
        if (isInchargeHR) return 'approved';
        return 'All'; // Regional sees All statuses (pending from PSOs + forwarded from self)
    }, [isHead, isInchargeHR]);

    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'All' | 'pending' | 'forwarded' | 'approved' | 'rejected' | 'submitted'>(
        isHead ? 'forwarded' : (isInchargeHR ? 'approved' : 'All')
    );
    const [psoFilter, setPsoFilter] = useState('All');
    const [locationFilter, setLocationFilter] = useState('All');
    const [dateFromFilter, setDateFromFilter] = useState('');
    const [dateToFilter, setDateToFilter] = useState('');

    // Draft Filter States for the filter modal
    const [draftStatusFilter, setDraftStatusFilter] = useState<'All' | 'pending' | 'forwarded' | 'approved' | 'rejected' | 'submitted'>('All');
    const [draftPsoFilter, setDraftPsoFilter] = useState('All');
    const [draftLocationFilter, setDraftLocationFilter] = useState('All');
    const [draftDateFromFilter, setDraftDateFromFilter] = useState('');
    const [draftDateToFilter, setDraftDateToFilter] = useState('');

    const [showFilterModal, setShowFilterModal] = useState(false);

    // Export Dropdown State
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Sync status filter if role/default changes
    useEffect(() => {
        setStatusFilter(isHead ? 'forwarded' : (isInchargeHR ? 'approved' : 'All'));
    }, [isHead, isInchargeHR]);

    // Toast State
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    // Calculate tomorrow and yesterday for input restriction
    const tomorrowStr = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d.toISOString().split('T')[0];
    }, []);

    const yesterdayStr = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().split('T')[0];
    }, []);

    // Resolve branch distance from standard structure
    const getBranchGeo = (b: Branch) => {
        let distance = '';
        if (b.location && b.location.distanceFromRegion) {
            distance = b.location.distanceFromRegion;
        } else if (b.distanceFromRegion) {
            distance = b.distanceFromRegion;
        }
        return distance.trim();
    };

    const parseDistanceValue = (distStr: string): number => {
        if (!distStr) return 0;
        const num = parseFloat(distStr.replace(/[^\d.]/g, ''));
        return isNaN(num) ? 0 : num;
    };

    const [managedPsoIds, setManagedPsoIds] = useState<string[]>([]);

    // Load Initial Data
    const loadData = async () => {
        if (!bankId) return;
        setLoading(true);
        try {
            const reqs = await fetchTravellingApprovalRequests(bankId);
            setRequests(reqs);

            if (isRegional && psoDocId) {
                // Step 1: resolve managed PSO IDs using the proven psoDocId path
                const ids = await fetchManagedPsoIds(bankId, psoDocId);
                setManagedPsoIds(ids);

                // Step 2: fetch ALL branches for the bank (no internal PSO filter)
                const { branches: allBranches } = await fetchBranchesData(
                    bankId,
                    true, // treat as bank user → returns all branches unfiltered
                    pso?.authUid,
                    pso?.designation
                );

                // Step 3: include Regional's own IDs + managed PSO IDs
                const branchAllowedIds = new Set<string>([
                    ...ids,
                    psoDocId,
                    ...(pso?.authUid ? [pso.authUid] : []),
                ]);
                const managedBranches = allBranches.filter(
                    b => b.psoId && branchAllowedIds.has(b.psoId)
                );
                setBranches(managedBranches);
            } else {
                // Bank / PSO / InchargeHR — use normal fetch
                const { branches: branchesData } = await fetchBranchesData(
                    pso?.authUid || psoDocId,
                    userRole === 'bank' || isInchargeHR,
                    pso?.authUid,
                    pso?.designation
                );
                setBranches(branchesData);
            }
        } catch (err) {
            console.error('Error loading Travelling Approval data:', err);
            showToast('Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        loadData();
    }, [bankId, psoDocId, isRegional]);

    // Auto-calculate Max Distance based on selection (can be edited by the user)
    useEffect(() => {
        const selectedObjs = branches.filter(b => selectedBranches.includes(b.id));
        const distances = selectedObjs.map(b => parseDistanceValue(getBranchGeo(b)));
        const maxDist = distances.length > 0 ? Math.max(...distances) : 0;
        setDistanceInput(maxDist.toString());
    }, [selectedBranches, branches]);

    // Handle Form Submit (Create or Edit)
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedBranches.length === 0) {
            showToast('Please select at least one branch', 'warning');
            return;
        }
        if (!dateOfVisit || !lastDateOfVisit) {
            showToast('Please select both visit dates', 'warning');
            return;
        }

        setSubmitting(true);
        try {
            const selectedObjs = branches.filter(b => selectedBranches.includes(b.id));
            const branchNames = selectedObjs.map(b => `${b.branchCode} - ${b.locationName || 'N/A'} (${b.address || 'N/A'})`);

            if (selectedRequest && selectedRequest.id) {
                // Edit mode
                await updateTravellingApprovalRequest(bankId, selectedRequest.id, {
                    branchIds: selectedBranches,
                    branchNames,
                    dateOfVisit,
                    lastDateOfVisit,
                    distance: parseFloat(distanceInput) || 0,
                    remarks,
                    purpose,
                    purposeOtherDetail: purpose === 'other' ? purposeOtherDetail : '',
                    travelModality,
                    nightStay,
                    amountClaim: parseFloat(amountClaim) || 0,
                });
                showToast('Travelling Approval Request updated successfully', 'success');
            } else {
                // Create mode — Regional submits directly as 'forwarded' (goes to Head); PSO submits as 'pending'
                const initialStatus = isRegional ? 'forwarded' : 'pending';
                await createTravellingApprovalRequest(bankId, {
                    branchIds: selectedBranches,
                    branchNames,
                    dateOfVisit,
                    lastDateOfVisit,
                    distance: parseFloat(distanceInput) || 0,
                    remarks,
                    purpose,
                    purposeOtherDetail: purpose === 'other' ? purposeOtherDetail : '',
                    travelModality,
                    nightStay,
                    amountClaim: parseFloat(amountClaim) || 0,
                    status: initialStatus,
                    psoId: psoDocId,
                    psoName: pso?.name || 'Unknown PSO',
                    psoEmail: pso?.email || 'N/A',
                });
                showToast(
                    isRegional
                        ? 'Request submitted — forwarded to Head for approval'
                        : 'Travelling Approval Request created successfully',
                    'success'
                );
            }

            setShowAddModal(false);
            // Reset form
            setSelectedRequest(null);
            setSelectedBranches([]);
            setDateOfVisit('');
            setLastDateOfVisit('');
            setBranchSearch('');
            setDistanceInput('0');
            setRemarks('');
            setPurpose('survey');
            setPurposeOtherDetail('');
            setTravelModality('');
            setNightStay(false);
            setAmountClaim('0');

            // Reload requests
            const reqs = await fetchTravellingApprovalRequests(bankId);
            setRequests(reqs);
        } catch (err) {
            console.error('Error saving request:', err);
            showToast('Failed to save request', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const [showRemarksModal, setShowRemarksModal] = useState(false);
    const [remarksTarget, setRemarksTarget] = useState<{ id: string; status: 'forwarded' | 'approved' | 'rejected' | 'submitted' } | null>(null);
    const [tempApproverRemarks, setTempApproverRemarks] = useState('');

    const triggerStatusUpdate = (id: string, status: 'forwarded' | 'approved' | 'rejected' | 'submitted') => {
        setRemarksTarget({ id, status });
        setTempApproverRemarks('');
        setShowRemarksModal(true);
    };

    // Handle status update (Approve/Reject/Forward/Submit)
    const handleStatusUpdate = async (id: string, status: 'forwarded' | 'approved' | 'rejected' | 'submitted', approverRemarks: string, confirmAmount?: number) => {
        setUpdatingId(id);
        try {
            await updateTravellingApprovalStatus(bankId, id, status, approverRemarks, confirmAmount);
            showToast(`Request ${status === 'forwarded' ? 'forwarded to Head' : status === 'submitted' ? 'submitted' : status} successfully`, 'success');

            // Close details modal and remarks modal if open
            setShowRemarksModal(false);
            setRemarksTarget(null);
            setShowAddModal(false);
            setSelectedRequest(null);
            setConfirmAmountInput('');

            // Reload requests
            const reqs = await fetchTravellingApprovalRequests(bankId);
            setRequests(reqs);
        } catch (err) {
            console.error('Error updating status:', err);
            showToast('Failed to update request status', 'error');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDeleteRequest = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this travelling approval request?')) return;
        setUpdatingId(id);
        try {
            await deleteTravellingApprovalRequest(bankId, id);
            showToast('Travelling Approval Request deleted successfully', 'success');

            // Close details modal if open
            setShowAddModal(false);
            setSelectedRequest(null);

            // Reload requests
            const reqs = await fetchTravellingApprovalRequests(bankId);
            setRequests(reqs);
        } catch (err) {
            console.error('Error deleting request:', err);
            showToast('Failed to delete request', 'error');
        } finally {
            setUpdatingId(null);
        }
    };

    // Filtered branches inside selection modal
    const filteredBranchesForSelection = useMemo(() => {
        if (!branchSearch) return branches;
        const term = branchSearch.toLowerCase();
        return branches.filter(b =>
            (b.address || '').toLowerCase().includes(term) ||
            (b.branchCode || '').toLowerCase().includes(term)
        );
    }, [branchSearch, branches]);

    // Unique filter options gathered from data
    const uniqueLocations = useMemo(() => {
        const locSet = new Set<string>();
        branches.forEach(b => {
            if (b.locationName) locSet.add(b.locationName);
        });
        return Array.from(locSet).sort();
    }, [branches]);

    const uniquePsoUsers = useMemo(() => {
        const psoMap = new Map<string, string>();
        requests.forEach(r => {
            if (r.psoId && r.psoName) {
                psoMap.set(r.psoId, r.psoName);
            }
        });
        return Array.from(psoMap.entries()).map(([id, name]) => ({ id, name }));
    }, [requests]);

    // Filter requests for the table view
    const filteredRequests = useMemo(() => {
        let list = requests;

        // If simple PSO, restrict list to their own requests
        if (isPSO) {
            list = list.filter(r => r.psoId === psoDocId);
        }

        // If Regional Head/Regional PSO, restrict to managed PSOs + their own requests
        if (isRegional) {
            const regionalAllowedIds = new Set<string>([
                ...managedPsoIds,
                psoDocId,                                // Regional's own Firestore doc ID
                ...(pso?.authUid ? [pso.authUid] : []), // Regional's own Auth UID
            ]);
            list = list.filter(r => regionalAllowedIds.has(r.psoId));
        }

        // If Head, restrict list to only forwarded requests
        if (isHead) {
            list = list.filter(r => r.status === 'forwarded');
        }

        // If Incharge HR, restrict to approved or submitted requests
        if (isInchargeHR) {
            list = list.filter(r => r.status === 'approved' || r.status === 'submitted');
        }

        // Apply status filter
        if (statusFilter !== 'All') {
            list = list.filter(r => r.status === statusFilter.toLowerCase());
        }

        // Apply PSO filter
        if (psoFilter !== 'All') {
            list = list.filter(r => r.psoId === psoFilter);
        }

        // Apply location filter
        if (locationFilter !== 'All') {
            list = list.filter(r => {
                return r.branchIds.some(id => {
                    const b = branches.find(x => x.id === id);
                    return b && b.locationName === locationFilter;
                });
            });
        }

        // Apply Date From and Date To filter
        if (dateFromFilter) {
            list = list.filter(r => r.dateOfVisit >= dateFromFilter);
        }
        if (dateToFilter) {
            list = list.filter(r => r.dateOfVisit <= dateToFilter);
        }

        // Apply search term (filter by request ID, branch, or PSO)
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(r => {
                const reqId = r.requestId || `TADA-${r.branchNames[0]?.split(' - ')[0] || 'UNK'}-${r.id?.slice(-4).toUpperCase() || 'XXXX'}`;
                if (reqId.toLowerCase().includes(term)) return true;
                if (r.psoName.toLowerCase().includes(term)) return true;
                if (r.branchNames.some(name => name.toLowerCase().includes(term))) return true;

                const matchBranch = r.branchIds.some(id => {
                    const b = branches.find(x => x.id === id);
                    if (!b) return false;
                    return (
                        (b.address || '').toLowerCase().includes(term) ||
                        (b.locationName || '').toLowerCase().includes(term) ||
                        (b.branchCode || '').toLowerCase().includes(term)
                    );
                });
                if (matchBranch) return true;

                return false;
            });
        }

        return list;
    }, [requests, isPSO, isRegional, isHead, isInchargeHR, psoDocId, managedPsoIds, statusFilter, psoFilter, locationFilter, dateFromFilter, dateToFilter, searchTerm, branches]);

    const hasActiveFilters = useMemo(() => {
        const isDefaultStatus = statusFilter === defaultStatus;
        return !isDefaultStatus || psoFilter !== 'All' || locationFilter !== 'All' || !!dateFromFilter || !!dateToFilter;
    }, [statusFilter, defaultStatus, psoFilter, locationFilter, dateFromFilter, dateToFilter]);

    const handleOpenFilterModal = () => {
        setDraftStatusFilter(statusFilter);
        setDraftPsoFilter(psoFilter);
        setDraftLocationFilter(locationFilter);
        setDraftDateFromFilter(dateFromFilter);
        setDraftDateToFilter(dateToFilter);
        setShowFilterModal(true);
    };

    const handleApplyFilters = () => {
        setStatusFilter(draftStatusFilter);
        setPsoFilter(draftPsoFilter);
        setLocationFilter(draftLocationFilter);
        setDateFromFilter(draftDateFromFilter);
        setDateToFilter(draftDateToFilter);
        setShowFilterModal(false);
    };

    const handleClearFilters = () => {
        setStatusFilter(defaultStatus);
        setPsoFilter('All');
        setLocationFilter('All');
        setDateFromFilter('');
        setDateToFilter('');

        setDraftStatusFilter(defaultStatus);
        setDraftPsoFilter('All');
        setDraftLocationFilter('All');
        setDraftDateFromFilter('');
        setDraftDateToFilter('');
        setShowFilterModal(false);
    };

    const handleExportExcel = () => {
        try {
            const dataToExport = filteredRequests.map(r => {
                const reqId = r.requestId || `TADA-${r.branchNames[0]?.split(' - ')[0] || 'UNK'}-${r.id?.slice(-4).toUpperCase() || 'XXXX'}`;
                return {
                    'Request ID': reqId,
                    ...(!isPSO ? {
                        'PSO Name': r.psoName,
                        'PSO Email': r.psoEmail
                    } : {}),
                    'Branches': r.branchNames.join('\n'),
                    'Purpose': r.purpose === 'other' ? `Other: ${r.purposeOtherDetail || ''}` : (r.purpose || '—'),
                    'Night Stay': r.nightStay ? 'Yes' : 'No',
                    'Amount Claim (Rs)': r.amountClaim || 0,
                    'Confirmed Amount (Rs)': r.confirmAmount !== undefined ? r.confirmAmount : '—',
                    'Date of Visit': formatDateGlobal(r.dateOfVisit),
                    'Last Date of Visit': formatDateGlobal(r.lastDateOfVisit),
                    'Distance (km)': r.distance,
                    'Status': r.status.toUpperCase(),
                    'Remarks': r.remarks || '',
                    'Approver Remarks': r.approverRemarks || '',
                };
            });

            const summaryPairs = [
                ['Report Title', 'Travelling Approval Report'],
                ['Total Requests', filteredRequests.length],
                ['Created By', isPSO ? (pso?.name || 'PSO') : `${reporterName} (${reporterDesignation})`],
                ['Status Filter', statusFilter === 'All' ? 'All Statuses' : statusFilter.toUpperCase()],
                ['Location Filter', locationFilter === 'All' ? 'All Locations' : locationFilter],
                ['Date Range', dateFromFilter && dateToFilter ? `${formatDateGlobal(dateFromFilter)} to ${formatDateGlobal(dateToFilter)}` : 'All Dates'],
                ['Generated On', formatDateGlobal(new Date())]
            ];

            const ws: XLSX.WorkSheet = {};
            const preamble = [
                ['Travelling Approval Report', ''],
                ['Report Summary', ''],
                ...summaryPairs,
                ['', '']
            ];
            XLSX.utils.sheet_add_aoa(ws, preamble, { origin: 'A1' });
            const nextRow = preamble.length + 1;
            XLSX.utils.sheet_add_json(ws, dataToExport, { origin: `A${nextRow}`, skipHeader: false });

            // Apply column widths dynamically
            const keys = Object.keys(dataToExport[0] || {});
            const cols = keys.map((key, colIdx) => {
                let max = key.length;
                for (const row of preamble) {
                    const cell = row[colIdx];
                    if (cell != null) {
                        max = Math.max(max, String(cell).length);
                    }
                }
                for (const r of dataToExport) {
                    const raw = (r as any)[key];
                    const s = raw != null ? String(raw) : '';
                    for (const line of s.split(/\r?\n/)) {
                        max = Math.max(max, line.length);
                    }
                }
                return { wch: Math.min(max + 2, 60) };
            });
            ws['!cols'] = cols;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Travelling Approvals');
            XLSX.writeFile(wb, `Travelling_Approvals_${new Date().toISOString().split('T')[0]}.xlsx`);
            showToast('Excel exported successfully', 'success');
        } catch (err) {
            console.error('Export Excel failed:', err);
            showToast('Failed to export Excel', 'error');
        }
    };

    const handleExportPDF = () => {
        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();

            // Title Banner
            doc.setFillColor(41, 128, 185);
            doc.rect(0, 0, pageW, 22, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.text('Travelling Approval Report', 12, 10);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(`Generated on ${formatDateGlobal(new Date())} · Active Filters Applied`, 12, 16);

            const summaryBody = [
                ['Report Title', 'Travelling Approval Report'],
                ['Total Requests', String(filteredRequests.length)],
                ['Created By', isPSO ? (pso?.name || 'PSO') : `${reporterName} (${reporterDesignation})`],
                ['Status Filter', statusFilter === 'All' ? 'All Statuses' : statusFilter.toUpperCase()],
                ['Location Filter', locationFilter === 'All' ? 'All Locations' : locationFilter],
                ['Date Range', dateFromFilter && dateToFilter ? `${formatDateGlobal(dateFromFilter)} to ${formatDateGlobal(dateToFilter)}` : 'All Dates'],
                ['Generated On', formatDateGlobal(new Date())]
            ];

            autoTable(doc, {
                startY: 26,
                head: [['Summary Field', 'Details']],
                body: summaryBody,
                theme: 'striped',
                styles: { fontSize: 8.5, cellPadding: 1.5, lineColor: [220, 224, 226], lineWidth: 0.1, textColor: 44 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 252, 253] },
                columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: pageW - 12 * 2 - 50 } },
                margin: { left: 12, right: 12 },
            });

            doc.addPage();

            // Draw header banner on page 2 for detail table
            doc.setFillColor(41, 128, 185);
            doc.rect(0, 0, pageW, 15, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.text('Travelling Approval Details', 12, 10);

            const tableHead = [[
                'Request ID',
                ...(!isPSO ? ['PSO Name', 'PSO Email'] : []),
                'Branches',
                'Purpose',
                'Night Stay',
                'Amount Claim',
                'Confirmed Amount',
                'Date of Visit',
                'Distance',
                'Status',
                'Remarks'
            ]];

            const tableBody = filteredRequests.map(r => {
                const reqId = r.requestId || `TADA-${r.branchNames[0]?.split(' - ')[0] || 'UNK'}-${r.id?.slice(-4).toUpperCase() || 'XXXX'}`;
                return [
                    reqId,
                    ...(!isPSO ? [r.psoName, r.psoEmail] : []),
                    r.branchNames.join('\n'),
                    r.purpose === 'other' ? `Other: ${r.purposeOtherDetail || ''}` : (r.purpose || '—'),
                    r.nightStay ? 'Yes' : 'No',
                    `Rs. ${(r.amountClaim || 0).toLocaleString()}`,
                    r.confirmAmount !== undefined ? `Rs. ${r.confirmAmount.toLocaleString()}` : '—',
                    formatDateGlobal(r.dateOfVisit),
                    `${r.distance} km`,
                    r.status.toUpperCase(),
                    r.remarks || ''
                ];
            });

            autoTable(doc, {
                startY: 19,
                head: tableHead,
                body: tableBody,
                theme: 'striped',
                styles: { fontSize: 8, cellPadding: 2, lineColor: [220, 224, 226], lineWidth: 0.1, textColor: 44 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [248, 249, 250] },
                margin: { left: 12, right: 12 },
            });

            doc.save(`Travelling_Approvals_${new Date().toISOString().split('T')[0]}.pdf`);
            showToast('PDF exported successfully', 'success');
        } catch (err) {
            console.error('Export PDF failed:', err);
            showToast('Failed to export PDF', 'error');
        }
    };

    const columns: Column<TravellingApprovalRequest>[] = [
        {
            header: 'Request ID',
            render: (r) => {
                const reqId = r.requestId || `TADA-${r.branchNames[0]?.split(' - ')[0] || 'UNK'}-${r.id?.slice(-4).toUpperCase() || 'XXXX'}`;
                return (
                    <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300">
                        {reqId}
                    </span>
                );
            }
        },
        ...(!isPSO ? [{
            header: 'PSO Information',
            render: (r: TravellingApprovalRequest) => (
                <div>
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{r.psoName}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{r.psoEmail}</div>
                </div>
            )
        }] : []),
        {
            header: 'Branches',
            render: (r) => {
                const displayBranches = r.branchNames.slice(0, 3);
                const hasMore = r.branchNames.length > 3;
                return (
                    <div className="flex flex-col gap-0.5 max-w-xs py-1">
                        {displayBranches.map((name, idx) => (
                            <div key={idx} className="text-xs text-gray-700 dark:text-gray-300 font-medium truncate">
                                {name}
                            </div>
                        ))}
                        {hasMore && (
                            <div className="text-xs text-gray-500 font-semibold italic">
                                ...
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Date of Visit',
            render: (r) => (
                <div className="flex items-center gap-2">
                    <Icon icon={FiCalendar} className="text-blue-500" />
                    <span>{formatDateGlobal(r.dateOfVisit)}</span>
                </div>
            )
        },
        {
            header: 'Last Date of Visit',
            render: (r) => (
                <div className="flex items-center gap-2">
                    <Icon icon={FiCalendar} className="text-gray-500" />
                    <span>{formatDateGlobal(r.lastDateOfVisit)}</span>
                </div>
            )
        },
        {
            header: 'Distance',
            render: (r) => (
                <div className="flex items-center gap-2">
                    <Icon icon={FiMapPin} className="text-indigo-500" />
                    <span className="font-medium">{r.distance} km</span>
                </div>
            )
        },
        {
            header: 'Purpose',
            render: (r) => (
                <span className="capitalize font-medium text-gray-800 dark:text-gray-200 text-xs">
                    {r.purpose === 'other' ? `Other: ${r.purposeOtherDetail || ''}` : (r.purpose || '—')}
                </span>
            )
        },
        {
            header: 'Night Stay',
            render: (r) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${r.nightStay
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                    {r.nightStay ? 'Yes' : 'No'}
                </span>
            )
        },
        {
            header: 'Claimed Amount',
            render: (r) => (
                <div className="flex flex-col text-xs min-w-[100px]">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                        Rs. {(r.amountClaim || 0).toLocaleString()}
                    </span>
                    {r.confirmAmount !== undefined && (
                        <span className="text-[10px] text-green-600 font-bold dark:text-green-400 mt-0.5">
                            Approved: Rs. {r.confirmAmount.toLocaleString()}
                        </span>
                    )}
                </div>
            )
        },
        {
            header: 'Remarks',
            render: (r) => (
                <div className="max-w-xs truncate text-xs text-gray-500 dark:text-gray-400" title={r.remarks || ''}>
                    <div>{r.remarks || '—'}</div>
                    {r.approverRemarks && (
                        <div className="text-[10px] text-indigo-500 mt-0.5">
                            Approver: {r.approverRemarks}
                        </div>
                    )}
                </div>
            )
        },
        {
            header: 'Status',
            render: (r) => {
                const colors = {
                    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
                    forwarded: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
                    approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
                    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                    submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                };
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium uppercase ${colors[r.status]}`}>
                        {r.status === 'submitted' ? 'submitted bill' : r.status}
                    </span>
                );
            }
        },
        {
            header: 'Actions',
            render: (r) => {
                const isBtnLoading = updatingId === r.id;

                const openDetails = (req: TravellingApprovalRequest) => {
                    setSelectedRequest(req);
                    setSelectedBranches(req.branchIds);
                    setDateOfVisit(req.dateOfVisit);
                    setLastDateOfVisit(req.lastDateOfVisit);
                    setDistanceInput(req.distance.toString());
                    setRemarks(req.remarks || '');
                    setPurpose(req.purpose || 'survey');
                    setPurposeOtherDetail(req.purposeOtherDetail || '');
                    setTravelModality(req.travelModality || '');
                    setNightStay(req.nightStay || false);
                    setAmountClaim((req.amountClaim ?? 0).toString());
                    setShowAddModal(true);
                };

                return (
                    <div className="flex items-center gap-1.5">
                        <Button
                            variant="secondary"
                            onClick={() => openDetails(r)}
                            title="View Details"
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-black/30 dark:hover:bg-black/50 dark:text-gray-300 p-1.5 rounded-lg flex items-center justify-center border-0"
                        >
                            <Icon icon={FiEye} className="h-4 w-4" />
                        </Button>

                        {(isPSO || isBank) && (r.status === 'pending') && (
                            <Button
                                variant="secondary"
                                onClick={() => openDetails(r)}
                                title={isBank ? "Edit Request" : "View/Edit"}
                                className="bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 dark:text-blue-300 p-1.5 rounded-lg flex items-center justify-center border-0"
                            >
                                <Icon icon={FiEdit2} className="h-4 w-4" />
                            </Button>
                        )}

                        {isRegional && r.status === 'pending' && (
                            <>
                                <Button
                                    variant="primary"
                                    onClick={() => triggerStatusUpdate(r.id!, 'forwarded')}
                                    disabled={isBtnLoading}
                                    title="Forward to Head"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white p-1.5 rounded-lg flex items-center justify-center"
                                >
                                    <Icon icon={FiCornerUpRight} className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => triggerStatusUpdate(r.id!, 'rejected')}
                                    disabled={isBtnLoading}
                                    title="Reject"
                                    className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-lg flex items-center justify-center border-0"
                                >
                                    <Icon icon={FiX} className="h-4 w-4" />
                                </Button>
                            </>
                        )}

                        {isHead && (r.status === 'pending' || r.status === 'forwarded') && (
                            <>
                                <Button
                                    variant="primary"
                                    onClick={() => triggerStatusUpdate(r.id!, 'approved')}
                                    disabled={isBtnLoading}
                                    title="Approve"
                                    className="bg-green-600 hover:bg-green-700 text-white p-1.5 rounded-lg flex items-center justify-center"
                                >
                                    <Icon icon={FiCheck} className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => triggerStatusUpdate(r.id!, 'rejected')}
                                    disabled={isBtnLoading}
                                    title="Reject"
                                    className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-lg flex items-center justify-center border-0"
                                >
                                    <Icon icon={FiX} className="h-4 w-4" />
                                </Button>
                            </>
                        )}

                        {isBank && (
                            <>
                                {r.status === 'pending' && (
                                    <Button
                                        variant="primary"
                                        onClick={() => triggerStatusUpdate(r.id!, 'forwarded')}
                                        disabled={isBtnLoading}
                                        title="Forward to Head"
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white p-1.5 rounded-lg flex items-center justify-center"
                                    >
                                        <Icon icon={FiCornerUpRight} className="h-4 w-4" />
                                    </Button>
                                )}
                                {(r.status === 'pending' || r.status === 'forwarded') && (
                                    <>
                                        <Button
                                            variant="primary"
                                            onClick={() => triggerStatusUpdate(r.id!, 'approved')}
                                            disabled={isBtnLoading}
                                            title="Approve"
                                            className="bg-green-600 hover:bg-green-700 text-white p-1.5 rounded-lg flex items-center justify-center"
                                        >
                                            <Icon icon={FiCheck} className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => triggerStatusUpdate(r.id!, 'rejected')}
                                            disabled={isBtnLoading}
                                            title="Reject"
                                            className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-lg flex items-center justify-center border-0"
                                        >
                                            <Icon icon={FiX} className="h-4 w-4" />
                                        </Button>
                                    </>
                                )}
                                <Button
                                    variant="secondary"
                                    onClick={() => handleDeleteRequest(r.id!)}
                                    disabled={isBtnLoading}
                                    title="Delete"
                                    className="bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/20 dark:hover:bg-red-900/40 dark:text-red-300 p-1.5 rounded-lg flex items-center justify-center border-0"
                                >
                                    <Icon icon={FiTrash2} className="h-4 w-4" />
                                </Button>
                            </>
                        )}

                        {(isBank || isInchargeHR) && r.status === 'approved' && (
                            <Button
                                variant="primary"
                                onClick={() => triggerStatusUpdate(r.id!, 'submitted')}
                                disabled={isBtnLoading}
                                title="Submitted Bill"
                                className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-lg flex items-center justify-center border-0"
                            >
                                <Icon icon={FiSend} className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                );
            }
        }
    ];

    const isAllowed = isPSO || isRegional || isHead || isBank || isInchargeHR;

    if (!isAllowed) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center p-6 bg-white dark:bg-black/30 rounded-xl border border-gray-200 dark:border-gray-700 max-w-md shadow-sm">
                    <h2 className="text-xl font-bold text-red-600 dark:text-red-400">Access Denied</h2>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        You do not have permission to view or manage Travelling Approvals.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
                    </div>
                </div>
            )}

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Travelling Approvals</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {(isPSO || isRegional) ? 'Create and track your travelling approval requests.' : 'Review and manage PSO travelling approvals.'}
                    </p>
                </div>
                {(isPSO || isRegional) && (
                    <Button
                        onClick={() => {
                            setSelectedRequest(null);
                            setSelectedBranches([]);
                            setDateOfVisit('');
                            setLastDateOfVisit('');
                            setBranchSearch('');
                            setDistanceInput('0');
                            setRemarks('');
                            setPurpose('survey');
                            setPurposeOtherDetail('');
                            setTravelModality('');
                            setNightStay(false);
                            setAmountClaim('0');
                            setShowAddModal(true);
                        }}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition"
                    >
                        <Icon icon={FiPlus} />
                        <span>Generate Request</span>
                    </Button>
                )}
            </div>
            {/* Filters & Actions Header */}
            <div className="flex flex-col md:flex-row gap-4 bg-white dark:bg-black/30 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm items-center justify-between">
                <div className="flex flex-1 w-full md:w-auto items-center gap-2">
                    <div className="relative flex-1">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearchTerm(searchInput.trim());
                                }
                            }}
                            placeholder="Search by Request ID, Branch, or PSO..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                    <Button
                        onClick={() => setSearchTerm(searchInput.trim())}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition"
                    >
                        Search
                    </Button>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                    {/* Clear Filters Button */}
                    {hasActiveFilters && (
                        <Button
                            variant="secondary"
                            onClick={handleClearFilters}
                            className="flex items-center gap-1.5 border border-red-200 hover:border-red-300 text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-900/30 px-3 py-2 rounded-xl text-sm font-semibold transition"
                        >
                            <Icon icon={FiX} />
                            <span>Clear Filters</span>
                        </Button>
                    )}

                    {/* Filters Button */}
                    <Button
                        variant="secondary"
                        onClick={handleOpenFilterModal}
                        className="flex items-center gap-1.5 border border-gray-300 dark:border-gray-600 bg-white hover:bg-gray-50 dark:bg-black/30 dark:hover:bg-black/50 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-xl text-sm font-semibold transition"
                    >
                        <Icon icon={FiSearch} />
                        <span>Filters</span>
                    </Button>

                    {/* Export Dropdown */}
                    <div className="relative" ref={exportMenuRef}>
                        <Button
                            variant="secondary"
                            onClick={() => setExportMenuOpen(!exportMenuOpen)}
                            className="flex items-center gap-1.5 border border-gray-300 dark:border-gray-600 bg-white hover:bg-gray-50 dark:bg-black/30 dark:hover:bg-black/50 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-xl text-sm font-semibold transition"
                        >
                            <Icon icon={FiDownload} />
                            <span>Export</span>
                            <Icon icon={FiChevronDown} />
                        </Button>
                        {exportMenuOpen && (
                            <div className="absolute right-0 mt-2 w-40 rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 py-1.5 z-40">
                                <button
                                    onClick={() => {
                                        handleExportExcel();
                                        setExportMenuOpen(false);
                                    }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
                                >
                                    <Icon icon={FiFileText} className="text-green-600 h-4 w-4" />
                                    <span>Export Excel</span>
                                </button>
                                <button
                                    onClick={() => {
                                        handleExportPDF();
                                        setExportMenuOpen(false);
                                    }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50"
                                >
                                    <Icon icon={FiFileText} className="text-red-600 h-4 w-4" />
                                    <span>Export PDF</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Requests Table */}
            <Table
                data={filteredRequests}
                columns={columns}
                keyExtractor={(item) => item.id || ''}
                loading={loading}
                emptyMessage="No travelling approval requests found."
            />

            {/* Create / Edit / View Request Modal */}
            <Modal
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setSelectedRequest(null);
                    setPurpose('survey');
                    setPurposeOtherDetail('');
                    setNightStay(false);
                    setAmountClaim('0');
                }}
                title={
                    !selectedRequest
                        ? "Generate Travelling Approval Request"
                        : (isBank || (isPSO && selectedRequest.status === 'pending') || (isRegional && selectedRequest.status === 'pending' && selectedRequest.psoId === psoDocId)
                            ? "Edit Travelling Approval Request"
                            : "View Travelling Approval Details")
                }
                size="md"
                footer={(
                    <div className="flex justify-end gap-3 px-6 py-4">
                        {/* Cancel Button */}
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => {
                                setShowAddModal(false);
                                setSelectedRequest(null);
                            }}
                            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white hover:bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 rounded-xl"
                        >
                            Cancel
                        </Button>

                        {/* Submit/Save for PSO / Regional / Bank editing/creating */}
                        {((isPSO && (!selectedRequest || selectedRequest.status === 'pending'))
                            || (isRegional && (!selectedRequest || (selectedRequest.status === 'pending' && selectedRequest.psoId === psoDocId)))
                            || isBank) && (
                            <Button
                                variant="primary"
                                type="submit"
                                form="travelling-approval-form"
                                disabled={submitting}
                                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                            >
                                {submitting ? 'Saving...' : (selectedRequest ? 'Save Changes' : 'Submit Request')}
                            </Button>
                        )}

                        {/* Actions for Regional Head / Regional PSO */}
                        {selectedRequest && isRegional && selectedRequest.status === 'pending' && (
                            <>
                                <Button
                                    variant="primary"
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => triggerStatusUpdate(selectedRequest.id!, 'forwarded')}
                                    className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                                >
                                    Forward to Head
                                </Button>
                                <Button
                                    variant="secondary"
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => triggerStatusUpdate(selectedRequest.id!, 'rejected')}
                                    className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white border-0 rounded-xl"
                                >
                                    Reject
                                </Button>
                            </>
                        )}

                        {/* Actions for Head */}
                        {selectedRequest && isHead && (selectedRequest.status === 'pending' || selectedRequest.status === 'forwarded') && (
                            <>
                                <Button
                                    variant="primary"
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => triggerStatusUpdate(selectedRequest.id!, 'approved')}
                                    className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-xl"
                                >
                                    Approve
                                </Button>
                                <Button
                                    variant="secondary"
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => triggerStatusUpdate(selectedRequest.id!, 'rejected')}
                                    className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white border-0 rounded-xl"
                                >
                                    Reject
                                </Button>
                            </>
                        )}

                        {/* Actions for Bank */}
                        {selectedRequest && isBank && (
                            <>
                                {selectedRequest.status === 'pending' && (
                                    <Button
                                        variant="primary"
                                        type="button"
                                        disabled={submitting}
                                        onClick={() => triggerStatusUpdate(selectedRequest.id!, 'forwarded')}
                                        className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                                    >
                                        Forward to Head
                                    </Button>
                                )}
                                {(selectedRequest.status === 'pending' || selectedRequest.status === 'forwarded') && (
                                    <>
                                        <Button
                                            variant="primary"
                                            type="button"
                                            disabled={submitting}
                                            onClick={() => triggerStatusUpdate(selectedRequest.id!, 'approved')}
                                            className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-xl"
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            disabled={submitting}
                                            onClick={() => triggerStatusUpdate(selectedRequest.id!, 'rejected')}
                                            className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white border-0 rounded-xl"
                                        >
                                            Reject
                                        </Button>
                                    </>
                                )}
                                <Button
                                    variant="secondary"
                                    type="button"
                                    disabled={submitting}
                                    onClick={() => handleDeleteRequest(selectedRequest.id!)}
                                    className="px-4 py-2 text-sm bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/20 dark:hover:bg-red-900/40 dark:text-red-300 border-0 font-semibold rounded-xl"
                                >
                                    Delete Request
                                </Button>
                            </>
                        )}

                        {/* Submitted Bill action inside details modal for Incharge HR / Bank */}
                        {selectedRequest && (isBank || isInchargeHR) && selectedRequest.status === 'approved' && (
                            <Button
                                variant="primary"
                                type="button"
                                disabled={submitting}
                                onClick={() => triggerStatusUpdate(selectedRequest.id!, 'submitted')}
                                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                            >
                                Submitted Bill
                            </Button>
                        )}
                    </div>
                )}
            >
                <form id="travelling-approval-form" onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
                    {/* Branch Select */}
                    <div className="flex flex-col gap-2 relative" ref={dropdownRef}>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Selected Branches</label>
                        <div
                            onClick={() => {
                                if (selectedRequest && selectedRequest.status !== 'pending' && !isBank && !isRegional) return;
                                if (!isPSO && !isBank && !isRegional) return;
                                setIsBranchDropdownOpen(!isBranchDropdownOpen);
                            }}
                            className={`flex min-h-[38px] w-full flex-wrap gap-1.5 items-center rounded-xl border border-gray-300 bg-gray-50 p-1.5 text-left text-sm text-gray-900 outline-none transition shadow-sm hover:bg-gray-100 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${((selectedRequest && selectedRequest.status !== 'pending' && !isBank && !isRegional) || (!isPSO && !isBank && !isRegional))
                                    ? 'cursor-not-allowed opacity-75'
                                    : 'cursor-pointer'
                                } focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                        >
                            {selectedBranches.length === 0 ? (
                                <span className="font-light text-gray-400 dark:text-gray-400 pl-2">No branches selected</span>
                            ) : (
                                selectedBranches.map(id => {
                                    const branch = branches.find(b => b.id === id);
                                    if (!branch) return null;
                                    return (
                                        <div
                                            key={id}
                                            className="flex items-center gap-2 bg-white border border-gray-200 dark:bg-black/50 dark:border-gray-700 px-2.5 py-1.5 rounded-xl text-xs"
                                        >
                                            <div className="flex flex-col text-left">
                                                <span className="font-bold text-gray-800 dark:text-gray-200">{branch.branchCode} - {branch.locationName || 'N/A'}</span>
                                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-light">{branch.address || 'N/A'}</span>
                                            </div>
                                            {(!selectedRequest || selectedRequest.status === 'pending' || isBank) && (isPSO || isBank || isRegional) && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedBranches(selectedBranches.filter(x => x !== id));
                                                    }}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <Icon icon={FiX} className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Branch Dropdown Panel */}
                        {isBranchDropdownOpen && (
                            <div className="absolute top-[100%] left-0 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 p-3 mt-1.5 flex flex-col gap-2">
                                <div className="relative">
                                    <Icon icon={FiSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={branchSearch}
                                        onChange={(e) => setBranchSearch(e.target.value)}
                                        placeholder="Search by branch code or address..."
                                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-black/30 outline-none text-xs text-gray-900 dark:text-gray-100 focus:border-blue-500"
                                    />
                                </div>
                                <div className="max-h-48 overflow-y-auto flex flex-col gap-1 pr-1">
                                    {filteredBranchesForSelection.length === 0 ? (
                                        <div className="text-center text-xs text-gray-400 py-4">No branches match query</div>
                                    ) : (
                                        filteredBranchesForSelection.map(b => {
                                            const isChecked = selectedBranches.includes(b.id);
                                            return (
                                                <div
                                                    key={b.id}
                                                    onClick={() => {
                                                        if (isChecked) {
                                                            setSelectedBranches(selectedBranches.filter(x => x !== b.id));
                                                        } else {
                                                            setSelectedBranches([...selectedBranches, b.id]);
                                                        }
                                                    }}
                                                    className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer text-xs"
                                                >
                                                    {/* Custom Checkbox: border only, no fill, black tick */}
                                                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isChecked
                                                            ? 'border-gray-800 dark:border-gray-200 bg-transparent'
                                                            : 'border-gray-300 dark:border-gray-600'
                                                        }`}>
                                                        {isChecked && (
                                                            <svg className="w-3.5 h-3.5 text-gray-800 dark:text-gray-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="20 6 9 17 4 12"></polyline>
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col text-left">
                                                        <span className="font-bold text-gray-800 dark:text-gray-200">{b.branchCode} - {b.locationName || 'N/A'}</span>
                                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-light">{b.address || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Visit Dates & Form Fields */}
                    {(() => {
                        const isFormDisabled = (!isPSO && !isBank && !isRegional) || (selectedRequest !== null && selectedRequest.status !== 'pending' && !isBank && !isRegional);
                        return (
                            <>
                                {/* Purpose Select */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Travel Modality</label>
                                        <select
                                            value={travelModality}
                                            onChange={(e) => setTravelModality(e.target.value)}
                                            disabled={isFormDisabled}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed capitalize"
                                            required
                                        >
                                            <option value="">Select Modality</option>
                                            <option value="By Air">By Air</option>
                                            <option value="By Train">By Train</option>
                                            <option value="By Bus">By Bus</option>
                                            <option value="By Own Car">By Own Car</option>
                                            <option value="By Rental Car / Taxi">By Rental Car / Taxi</option>
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Purpose</label>
                                        <select
                                            value={purpose}
                                            onChange={(e) => setPurpose(e.target.value)}
                                            disabled={isFormDisabled}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed capitalize"
                                        >
                                            <option value="survey">Survey</option>
                                            <option value="security check">Security Check</option>
                                            <option value="coordination">Coordination</option>
                                            <option value="in-quiry">In-quiry</option>
                                            <option value="contingency travel">Contingency Travel</option>
                                            <option value="routine inspection">Routine Inspection</option>
                                            <option value="planned">Planned</option>
                                            <option value="on executive instruction">On Executive Instruction</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    {purpose === 'other' && (
                                        <div className="flex flex-col gap-2 md:col-span-2">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Specify Purpose</label>
                                            <input
                                                type="text"
                                                value={purposeOtherDetail}
                                                onChange={(e) => setPurposeOtherDetail(e.target.value)}
                                                required={purpose === 'other'}
                                                disabled={isFormDisabled}
                                                placeholder="Please specify..."
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Visit</label>
                                    <input
                                        type="date"
                                        min={tomorrowStr}
                                        value={dateOfVisit}
                                        onChange={(e) => setDateOfVisit(e.target.value)}
                                        required
                                        disabled={isFormDisabled}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed"
                                    />
                                </div>

                                {/* Last Date of Visit */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Last Date of Visit</label>
                                    <input
                                        type="date"
                                        max={yesterdayStr}
                                        value={lastDateOfVisit}
                                        onChange={(e) => setLastDateOfVisit(e.target.value)}
                                        required
                                        disabled={isFormDisabled}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed"
                                    />
                                </div>

                                {/* Distance (Editable) */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Distance (KM)</label>
                                    <TextField
                                        type="number"
                                        value={distanceInput}
                                        onChange={(e) => setDistanceInput(e.target.value)}
                                        placeholder="Enter distance in km"
                                        disabled={isFormDisabled}
                                    />
                                </div>

                                {/* Night Stay & Amount Claim */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center justify-between border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 p-3 shadow-sm">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Night Stay</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                                {nightStay ? 'Yes' : 'No'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => !isFormDisabled && setNightStay(!nightStay)}
                                                disabled={isFormDisabled}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${nightStay ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                                                    } ${isFormDisabled ? 'opacity-75 cursor-not-allowed' : ''}`}
                                            >
                                                <span
                                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${nightStay ? 'translate-x-5' : 'translate-x-0'
                                                        }`}
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Amount Claim (Rs.)</label>
                                        <TextField
                                            type="number"
                                            value={amountClaim}
                                            onChange={(e) => setAmountClaim(e.target.value)}
                                            placeholder="Enter claim amount"
                                            disabled={isFormDisabled}
                                            required
                                        />
                                    </div>
                                </div>

                                {selectedRequest && selectedRequest.confirmAmount !== undefined && (
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-semibold text-green-700 dark:text-green-400">Confirmed Amount</label>
                                        <div className="px-3 py-2 border border-green-250 dark:border-green-800 rounded-xl bg-green-50 dark:bg-green-900/10 text-sm font-bold text-green-800 dark:text-green-300">
                                            Rs. {selectedRequest.confirmAmount.toLocaleString()}
                                        </div>
                                    </div>
                                )}

                                {/* Remarks */}
                                <div className="flex flex-col gap-2">
                                    <label className={`text-sm font-semibold ${purpose === 'contingency travel' ? 'text-red-500' : 'text-gray-700 dark:text-gray-200'}`}>
                                        Remarks {purpose === 'contingency travel' ? '(Mandatory for Contingency Travel)' : '*'}
                                    </label>
                                    <textarea
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        disabled={isFormDisabled}
                                        placeholder="Enter any remarks..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed"
                                        rows={3}
                                        required
                                    />
                                </div>
                            </>
                        );
                    })()}

                    {/* Approver Remarks */}
                    {selectedRequest && selectedRequest.approverRemarks && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Approver Remarks</label>
                            <textarea
                                value={selectedRequest.approverRemarks}
                                readOnly
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 disabled:opacity-75"
                                rows={3}
                            />
                        </div>
                    )}
                </form>
            </Modal>

            {/* Remarks UI Prompt Modal */}
            <Modal
                isOpen={showRemarksModal}
                onClose={() => {
                    setShowRemarksModal(false);
                    setRemarksTarget(null);
                    setConfirmAmountInput('');
                }}
                title={
                    remarksTarget?.status === 'forwarded'
                        ? 'Forward to Head'
                        : remarksTarget?.status === 'approved'
                            ? 'Approve Request'
                            : remarksTarget?.status === 'submitted'
                                ? 'Submit Bill'
                                : 'Reject Request'
                }
                size="sm"
            >
                <div className="p-6 flex flex-col gap-4">
                    {isInchargeHR && remarksTarget?.status === 'submitted' && (() => {
                        const targetRequest = requests.find(r => r.id === remarksTarget?.id);
                        const targetAmountClaim = targetRequest?.amountClaim ?? 0;
                        return (
                            <>
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Amount Claim
                                    </label>
                                    <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-100 dark:bg-gray-800 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        Rs. {targetAmountClaim.toLocaleString()}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Confirm Amount <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        value={confirmAmountInput}
                                        onChange={(e) => setConfirmAmountInput(e.target.value)}
                                        placeholder="Enter confirmed amount"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500"
                                    />
                                </div>
                            </>
                        );
                    })()}

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Remarks (Optional)
                        </label>
                        <textarea
                            value={tempApproverRemarks}
                            onChange={(e) => setTempApproverRemarks(e.target.value)}
                            placeholder="Enter remarks..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-black/30 outline-none text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500"
                            rows={3}
                        />
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => {
                                setShowRemarksModal(false);
                                setRemarksTarget(null);
                                setConfirmAmountInput('');
                            }}
                            className="px-4 py-2 text-sm border-0 bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="button"
                            disabled={updatingId !== null}
                            onClick={() => {
                                if (remarksTarget) {
                                    if (isInchargeHR && remarksTarget.status === 'submitted') {
                                        if (!confirmAmountInput || isNaN(parseFloat(confirmAmountInput))) {
                                            showToast('Please enter a valid Confirm Amount', 'warning');
                                            return;
                                        }
                                    }
                                    handleStatusUpdate(
                                        remarksTarget.id,
                                        remarksTarget.status,
                                        tempApproverRemarks,
                                        isInchargeHR && remarksTarget.status === 'submitted' ? parseFloat(confirmAmountInput) : undefined
                                    );
                                }
                            }}
                            className={`px-4 py-2 text-sm text-white ${remarksTarget?.status === 'forwarded'
                                    ? 'bg-indigo-600 hover:bg-indigo-700'
                                    : remarksTarget?.status === 'approved'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : remarksTarget?.status === 'submitted'
                                            ? 'bg-blue-600 hover:bg-blue-700'
                                            : 'bg-red-600 hover:bg-red-700'
                                }`}
                        >
                            {updatingId !== null ? 'Processing...' : (
                                remarksTarget?.status === 'forwarded'
                                    ? 'Forward'
                                    : remarksTarget?.status === 'approved'
                                        ? 'Approve'
                                        : remarksTarget?.status === 'submitted'
                                            ? 'Submit'
                                            : 'Reject'
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Filter Modal */}
            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title="Filters"
                size="sm"
            >
                <div className="p-5 flex flex-col gap-4">
                    {/* Status Filter */}
                    {!isHead && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Status</label>
                            <select
                                value={draftStatusFilter}
                                onChange={(e) => setDraftStatusFilter(e.target.value as any)}
                                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            >
                                {isInchargeHR ? (
                                    <>
                                        <option value="approved">Approved</option>
                                        <option value="submitted">Submitted</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="All">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="forwarded">Forwarded to Head</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="submitted">Submitted Bill</option>
                                    </>
                                )}
                            </select>
                        </div>
                    )}

                    {/* PSO Filter */}
                    {(isBank || isHead || isRegional) && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">PSO User</label>
                            <select
                                value={draftPsoFilter}
                                onChange={(e) => setDraftPsoFilter(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            >
                                <option value="All">All PSOs</option>
                                {uniquePsoUsers.map(u => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Location/Region Filter */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Branch Location / Region</label>
                        <select
                            value={draftLocationFilter}
                            onChange={(e) => setDraftLocationFilter(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        >
                            <option value="All">All Locations</option>
                            {uniqueLocations.map(loc => (
                                <option key={loc} value={loc}>{loc}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date range Filter */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Date range</label>
                        <DatePicker.RangePicker
                            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            value={
                                draftDateFromFilter && draftDateToFilter
                                    ? [dayjs(draftDateFromFilter), dayjs(draftDateToFilter)]
                                    : null
                            }
                            allowClear
                            format="MMMM D, YYYY"
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) {
                                    setDraftDateFromFilter('');
                                    setDraftDateToFilter('');
                                    return;
                                }
                                setDraftDateFromFilter(dates[0].format('YYYY-MM-DD'));
                                setDraftDateToFilter(dates[1].format('YYYY-MM-DD'));
                            }}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2.5 mt-2">
                        <Button
                            variant="secondary"
                            onClick={handleClearFilters}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 border-0 px-4 py-2 rounded-xl text-sm font-semibold transition"
                        >
                            Reset
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleApplyFilters}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition"
                        >
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
